import {ChangeEvent, memo, useCallback, useEffect, useState} from "react";
import {Button, Menu, MenuHandler, MenuItem, MenuList} from "@material-tailwind/react";
import {FooterListItem} from "./Sidebar/FooterMenu";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {DropdownInputProps} from "./DropdownInput";
import Loader from "../assets/icons/Loader";
import {useTranslation} from "react-i18next";
import useOutsideAlerter from "../hooks/useOutsideAlerter";

export interface AjaxFooterListItem extends FooterListItem {
    description?: string | JSX.Element
}

interface AjaxDropdownInputProps extends DropdownInputProps {
    list: AjaxFooterListItem[],
    onSearch: (s: string) => void,
    searchPlaceholder?: string
}

const AjaxDropdownInput = ({
                               name,
                               className,
                               list,
                               selected,
                               onChange,
                               buttonClassName,
                               menuClassName,
                               labelClassName,
                               id,
                               searchable = false,
                               multiple = false,
                               withLabel = true,
                               onRemove,
                               onSearch,
                               searchPlaceholder
                           }: AjaxDropdownInputProps) => {

    const {t} = useTranslation();
    const [localList, setLocalList] = useState<AjaxFooterListItem[]>([]);
    const [search, setSearch] = useState<string>('');
    const [inProgress, setInProgress] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const {ref, isShown} = useOutsideAlerter(setOpen);

    const singleLabel = useCallback(() => {
        const item = list.find(i => i.id ? i.id === selected : (i.code ? i.code === selected : i.name === selected));
        if (item) {
            return <>{(item.icon ?? '')}{item.name}</>;
        }
        return '';
    }, [list, selected]);

    const onLocalRemove = useCallback((index: number) => {
        if (onRemove) {
            onRemove(index);
        }
    }, [onRemove])

    const multipleLabel = useCallback(() => {
        if (selected && Array.isArray(selected) && !withLabel)
            return <>{name} ({selected.length})</>
        return selected && Array.isArray(selected) && selected.length > 0 ? selected.map((id, index) => {
            const item = list.find(i => i.id === id);
            if (!item)
                return <></>;
            return <span key={`dropdown-input-${name}-${index}`}
                         className="bg-blue-gray-50 dark:bg-black dark:text-white px-1.5 py-0.5 mr-1 mb-1 inline-block min-w-[4rem] rounded-md">
                <span className="flex items-center w-fit">
                {item.icon ?? ''}{item.name}
                    <i className="ml-1 text-red-400" onClick={() => onLocalRemove(index)}><FontAwesomeIcon
                        icon={solid('xmark')}
                        className="w-5"/></i>
                    </span>
            </span>
        }) : <></>
    }, [list, selected, name]);

    const label = useCallback(() => {
        if (!multiple)
            return singleLabel();
        return multipleLabel();
    }, [list, selected]);

    const onSearchChanged = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget) {
            setSearch(e.currentTarget.value);
            setInProgress(true);
            onSearch(e.currentTarget.value);
        }
    }

    const onItemClicked = useCallback((index: number) => {
        const i = localList[index];
        setSearch('');
        if (!i)
            return;
        if (multiple && Array.isArray(selected) && selected.includes(i.id ?? 0)) {
            onLocalRemove(selected.indexOf(i.id ?? 0));
        } else {
            onChange(i.id ?? (i.code ?? i.name))
        }
        setOpen(false);
    }, [localList, selected, onChange]);

    useEffect(() => {
        setLocalList(searchable && list.length > 10 ? list.slice(0, 10) : list);
        setInProgress(false);
    }, [list, searchable]);


    return <div id={id ? '#' + id : ''}>
        <Menu dismiss={{itemPress: false}} open={open && isShown}>
            <MenuHandler>
                <div
                    className={`relative ${multiple ? 'min-h-[2.5rem]' : 'h-10'} ${className ?? 'w-full min-w-[200px]'}`}>
                    <Button placeholder={''} onClick={() => setOpen(!open)}
                            className={`normal-case ${multiple ? 'min-h-[2.5rem]' : 'flex'} items-center text-left w-full peer h-full bg-transparent text-blue-gray-700 font-sans font-normal outline outline-0 focus:outline-0 disabled:bg-blue-gray-50 disabled:border-0 disabled:cursor-not-allowed transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 border focus:border-2 border-t-transparent focus:border-t-transparent placeholder:opacity-0 focus:placeholder:opacity-100 text-sm px-3 py-2.5 rounded-[7px] border-blue-gray-200 focus:border-gray-900 dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent shadow-none hover:shadow-none ${buttonClassName ?? ''}`}>{label()}</Button>
                    <label
                        className={`flex w-full h-full select-none pointer-events-none absolute left-0 font-normal !overflow-visible truncate peer-placeholder-shown:text-blue-gray-500 leading-tight peer-focus:leading-tight peer-disabled:text-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500 transition-all -top-1.5 peer-placeholder-shown:text-sm text-[11px] peer-focus:text-[11px] before:content[' '] before:block before:box-border before:w-2.5 before:h-1.5 before:mt-[6.5px] before:mr-1 peer-placeholder-shown:before:border-transparent before:rounded-tl-md before:border-t peer-focus:before:border-t-2 before:border-l peer-focus:before:border-l-2 before:pointer-events-none before:transition-all peer-disabled:before:border-transparent after:content[' '] after:block after:flex-grow after:box-border after:w-2.5 after:h-1.5 after:mt-[6.5px] after:ml-1 peer-placeholder-shown:after:border-transparent after:rounded-tr-md after:border-t peer-focus:after:border-t-2 after:border-r peer-focus:after:border-r-2 after:pointer-events-none after:transition-all peer-disabled:after:border-transparent peer-placeholder-shown:leading-[3.75] text-gray-500 peer-focus:text-gray-900 before:border-blue-gray-200 peer-focus:before:!border-gray-900 after:border-blue-gray-200 peer-focus:after:!border-gray-900 dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800 ${labelClassName ?? ''}`}>{name}</label>
                </div>
            </MenuHandler>
            <MenuList
                ref={ref}
                className={`max-h-[250px] max-w-md mt-2 dark:bg-blue-gray-900 dark:text-gray-500 dark:border-blue-gray-800 ${menuClassName ?? 'w-full'}`}>
                {searchable && <input
                    className="w-full px-3 py-1 rounded border border-gray-700 dark:border-blue-gray-800 mb-2 bg-transparent outline-none placeholder:opacity-100 placeholder:text-gray-700"
                    placeholder={searchPlaceholder ?? t('admin.datatable.search')} value={search} onChange={onSearchChanged}/>}
                {searchable && !inProgress && list.length > 10 &&
                    <div className="text-xs px-2 mb-2 text-gray-700 dark:text-gray-700 outline-none">{t('admin.datatable.only_10')}</div>}
                {searchable && inProgress &&
                    <div className="text-xs px-2 mb-2 text-gray-700 dark:text-gray-700 outline-none text-center">
                        <Loader/></div>}
                {!inProgress ? localList.map((i, index) => {
                    return <MenuItem
                        className={`${multiple && Array.isArray(selected) && selected.includes(i.id ?? 0) ? 'bg-blue-gray-50 bg-opacity-80 text-blue-gray-900' : ''}`}
                        key={`${i.name.toString()}_${i.id?.toString()}_${i.code}`}
                        onClick={() => onItemClicked(index)}>
                        <div className="w-full block font-bold text-md mb-2">{i.icon}{i.name}</div>
                        <div className="w-full block">
                            {i.description}
                        </div>
                    </MenuItem>
                }) : ''}
            </MenuList>
        </Menu>
    </div>
}

export default memo(AjaxDropdownInput);
